import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Mail, ArrowRight, Heart } from "lucide-react";

const LINKS = {
  Library: [
    { label: "Browse All Books",  path: "/search" },
    { label: "My Reading Shelf",  path: "/my-library" },
    { label: "Theology",          path: "/search?q=Theology" },
    { label: "Devotional",        path: "/search?q=Devotional" },
    { label: "Apologetics",       path: "/search?q=Apologetics" },
  ],
  Store: [
    { label: "Hardcopy Books",    path: "/store" },
    { label: "New Arrivals",      path: "/store" },
    { label: "Shopping Cart",     path: "/cart" },
  ],
  Support: [
    { label: "FAQ",               action: () => alert("Ebenezer Books is a free digital library for classic Christian literature.") },
    { label: "Contact Us",        action: () => alert("Email: support@ebenezerbooks.com") },
    { label: "Privacy Policy",    action: () => alert("We do not track your reading habits. Bookmarks are saved locally.") },
    { label: "Terms of Service",  action: () => alert("All books are in the public domain and free to read.") },
  ],
};

export default function Footer() {
  const navigate    = useNavigate();
  const [email,      setEmail]      = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email.trim()) { setSubscribed(true); setEmail(""); setTimeout(() => setSubscribed(false), 5000); }
  };

  return (
    <footer style={{ background: "var(--footer-bg)", color: "var(--footer-text)", marginTop: "auto" }}>

      {/* ── MAIN COLUMNS ── */}
      <div style={{ borderBottom: "1px solid var(--footer-border)" }}>
        <div className="footer-grid" style={{
          maxWidth: "var(--max-w)", margin: "0 auto", padding: "3.5rem 2rem 3rem",
          display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: "3rem",
        }}>

          {/* Brand column */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "1.25rem" }}>
              <div style={{
                width: "36px", height: "36px",
                background: "linear-gradient(135deg, var(--gold), var(--gold-light))",
                borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <BookOpen size={17} color="#fff" strokeWidth={2} />
              </div>
              <div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: "1.25rem", fontWeight: 700, color: "var(--footer-text)", lineHeight: 1 }}>
                  Ebenezer Books
                </div>
                <div style={{ fontSize: "0.58rem", color: "var(--footer-muted)", letterSpacing: "0.12em", textTransform: "uppercase", marginTop: "2px" }}>
                  Library &amp; Bookstore
                </div>
              </div>
            </div>
            <p style={{ fontSize: "0.82rem", color: "var(--footer-muted)", lineHeight: 1.75, marginBottom: "1.5rem", maxWidth: "280px" }}>
              A curated digital library dedicated to classic Christian literature and theological writings — free to read, beautiful to own.
            </p>

            {/* Newsletter */}
            <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--footer-muted)", textTransform: "uppercase", letterSpacing: "0.09em", marginBottom: "0.6rem", display: "flex", alignItems: "center", gap: "6px" }}>
              <Mail size={12} /> Newsletter
            </div>
            {subscribed ? (
              <div style={{ fontSize: "0.82rem", color: "var(--footer-accent)", fontWeight: 500 }}>
                ✓ Subscribed — thank you!
              </div>
            ) : (
              <form onSubmit={handleSubscribe} style={{ display: "flex", gap: "6px" }}>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com" required
                  style={{
                    flex: 1, padding: "8px 14px", borderRadius: "var(--radius-full)",
                    border: "1px solid var(--footer-input-border)",
                    background: "var(--footer-input-bg)",
                    color: "var(--footer-text)", fontSize: "0.82rem", outline: "none",
                    fontFamily: "var(--font-body)", transition: "border-color 0.15s",
                  }}
                  onFocus={(e) => e.target.style.borderColor = "var(--gold)"}
                  onBlur={(e)  => e.target.style.borderColor = "var(--footer-input-border)"}
                />
                <button type="submit" style={{
                  padding: "8px 16px", borderRadius: "var(--radius-full)", border: "none",
                  background: "linear-gradient(135deg, var(--gold), var(--gold-2))",
                  color: "#fff", fontWeight: 700, fontSize: "0.78rem", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: "4px", flexShrink: 0,
                }}>Join <ArrowRight size={11} /></button>
              </form>
            )}
          </div>

          {/* Link columns */}
          {Object.entries(LINKS).map(([title, links]) => (
            <div key={title}>
              <h4 style={{
                fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.12em",
                color: "var(--footer-accent)", marginBottom: "1.1rem", fontWeight: 700,
              }}>
                {title}
              </h4>
              <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "9px" }}>
                {links.map(({ label, path, action }) => (
                  <li key={label}>
                    <span
                      onClick={action || (() => navigate(path))}
                      style={{ fontSize: "0.82rem", color: "var(--footer-muted)", cursor: "pointer", transition: "color 0.15s" }}
                      onMouseEnter={(e) => e.currentTarget.style.color = "var(--footer-text)"}
                      onMouseLeave={(e) => e.currentTarget.style.color = "var(--footer-muted)"}
                    >{label}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* ── BOTTOM BAR ── */}
      <div style={{ borderTop: "1px solid var(--footer-divider)" }}>
        <div style={{
          maxWidth: "var(--max-w)", margin: "0 auto", padding: "1.25rem 2rem",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: "0.75rem",
          fontSize: "0.74rem", color: "var(--footer-muted-2)",
        }}>
          <span>© {new Date().getFullYear()} Ebenezer Books · Public Domain Classical Library</span>
          <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            Made with <Heart size={10} color="#8B3A2A" fill="#8B3A2A" /> for the Kingdom · <em>Soli Deo Gloria</em>
          </span>
        </div>
      </div>

      <style>{`
        @media(max-width: 900px) { .footer-grid { grid-template-columns: 1fr 1fr !important; } }
        @media(max-width: 520px) { .footer-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </footer>
  );
}
